import React, { useState, useMemo, useCallback } from 'react';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  GridActionsCellItem,
  GridCellParams,
  GridRowId,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { formatDate } from '../../../utils/dateUtils';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';

export interface Guide {
  id: string;
  name: string;
  type: string;
  characterClass: string;
  teamId?: string;
  teamName?: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
}

export interface Discord {
  id: string;
  name: string;
  invite_url?: string;
  created_at?: string;
}

export interface GuidesTableProps {
  guides: Guide[];
  discords?: Discord[];
  onEditGuide: (guide: Guide) => void;
  onDeleteGuide: (guideId: string) => void;
  onTogglePublished: (guideId: string, published: boolean) => void;
  isAdmin: boolean;
}

const useStyles = makeStyles<Theme>((theme) => ({
  deleteButton: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    justifyContent: 'flex-start',
  },
}));

const GuidesTable: React.FC<GuidesTableProps> = ({
  guides,
  discords = [],
  onEditGuide,
  onDeleteGuide,
  onTogglePublished,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [deleteSelectedDialogOpen, setDeleteSelectedDialogOpen] = useState(false);

  // Helper function to get Discord team name by ID
  const getDiscordTeamName = useCallback(
    (teamId?: string): string => {
      if (!teamId) return t('guides.table.noTeam');
      const discord = discords.find((d) => d.id === teamId);
      return discord ? discord.name : t('guides.table.noTeam');
    },
    [discords, t]
  );

  const handleDeleteSelectedClick = () => {
    setDeleteSelectedDialogOpen(true);
  };

  const handleDeleteSelectedConfirm = () => {
    selectedRows.forEach((id) => {
      onDeleteGuide(id.toString());
    });
    setSelectedRows([]);
    setDeleteSelectedDialogOpen(false);
  };

  const handleDeleteSelectedCancel = () => {
    setDeleteSelectedDialogOpen(false);
  };

  const getGuideTypeLabel = useCallback(
    (type: string): string => {
      switch (type) {
        case 'general':
          return t('guides.type.general');
        case 'pvp':
          return t('guides.type.pvp');
        case 'pvm':
          return t('guides.type.pvm');
        default:
          return type;
      }
    },
    [t]
  );

  const getClassLabel = useCallback(
    (className: string): string => {
      switch (className) {
        case 'any':
          return t('guides.class.any');
        case 'barbarian':
          return t('guides.class.barbarian');
        case 'sorceress':
          return t('guides.class.sorceress');
        case 'amazon':
          return t('guides.class.amazon');
        case 'assassin':
          return t('guides.class.assassin');
        case 'necromancer':
          return t('guides.class.necromancer');
        case 'paladin':
          return t('guides.class.paladin');
        case 'druid':
          return t('guides.class.druid');
        default:
          return className;
      }
    },
    [t]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('guides.table.name'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'type',
        headerName: t('guides.table.type'),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={getGuideTypeLabel(params.value as string)}
            color={
              params.value === 'build'
                ? 'primary'
                : params.value === 'strategy'
                  ? 'secondary'
                  : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'characterClass',
        headerName: t('guides.table.class'),
        width: 150,
        renderCell: (params) => (
          <Chip label={getClassLabel(params.value as string)} color="info" size="small" />
        ),
      },
      {
        field: 'teamId',
        headerName: t('guides.table.team'),
        width: 150,
        valueGetter: (params: GridValueGetterParams) => {
          // If teamName is available, use it (for backward compatibility)
          if (params.row.teamName) {
            return params.row.teamName;
          }
          // Otherwise use the teamId to find the team name
          return getDiscordTeamName(params.row.teamId);
        },
      },
      {
        field: 'createdAt',
        headerName: t('guides.table.createdAt'),
        width: 150,
        valueGetter: (params: GridValueGetterParams) => formatDate(params.row.createdAt),
      },
      {
        field: 'published',
        headerName: t('guides.table.status'),
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.value ? t('guides.status.published') : t('guides.status.unpublished')}
            color={params.value ? 'success' : 'error'}
            size="small"
          />
        ),
      },
    ],
    [t, getClassLabel, getGuideTypeLabel, getDiscordTeamName]
  );

  const actionsColumn = useMemo(
    () => ({
      field: 'actions',
      headerName: t('common.dataGrid.rowActions'),
      flex: 0.8,
      minWidth: 150,
      renderCell: (params: GridCellParams) => {
        const guideId = params.row.id as string;
        const published = params.row.published as boolean;
        const guide = params.row as Guide;

        const actions: React.ReactNode[] = [];

        if (isAdmin) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label={t('common.dataGrid.edit')}
              onClick={() => onEditGuide(guide)}
              showInMenu
            />
          );

          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label={t('common.dataGrid.delete')}
              onClick={() => onDeleteGuide(guideId)}
              showInMenu
            />
          );
        }

        if (isAdmin) {
          actions.push(
            <GridActionsCellItem
              key="publish"
              icon={published ? <VisibilityOffIcon /> : <VisibilityIcon />}
              label={published ? t('guides.table.unpublish') : t('guides.table.publish')}
              onClick={() => onTogglePublished(guideId, !published)}
              showInMenu
            />
          );
        }

        return actions;
      },
    }),
    [onEditGuide, onDeleteGuide, onTogglePublished, isAdmin, t]
  );

  const allColumns = useMemo(() => [...columns, actionsColumn], [columns, actionsColumn]);

  return (
    <Box>
      <DataGrid
        rows={guides}
        columns={allColumns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        autoHeight
        checkboxSelection={isAdmin}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection);
        }}
        localeText={{
          noRowsLabel: t('common.dataGrid.noRows'),
          noResultsOverlayLabel: t('common.dataGrid.noResults'),
          toolbarColumns: t('common.dataGrid.toolbar.columns'),
          toolbarExport: t('common.dataGrid.toolbar.export'),
          toolbarFilters: t('common.dataGrid.toolbar.search'),
          columnMenuLabel: t('common.dataGrid.columnMenu.columns'),
          columnMenuShowColumns: t('common.dataGrid.columnMenu.columns'),
          columnMenuFilter: t('common.dataGrid.columnMenu.filter'),
          columnMenuHideColumn: t('common.dataGrid.columnMenu.hideColumn'),
          columnMenuSortAsc: `${t('common.dataGrid.columnMenu.sort')} (A-Z)`,
          columnMenuSortDesc: `${t('common.dataGrid.columnMenu.sort')} (Z-A)`,
          MuiTablePagination: {
            labelRowsPerPage: t('common.dataGrid.pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('common.dataGrid.pagination.of')} ${count}`,
          },
        }}
      />

      {isAdmin && (
        <Box className={classes.buttonContainer}>
          {selectedRows.length > 0 && (
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteSelectedClick}
              className={classes.deleteButton}
              size="medium"
            >
              {t('guides.table.deleteSelected', { count: selectedRows.length })}
            </Button>
          )}
        </Box>
      )}

      <ConfirmDialog
        open={deleteSelectedDialogOpen}
        title={t('guides.dialog.deleteSelectedTitle')}
        content={t('guides.dialog.deleteSelectedContent', { count: selectedRows.length })}
        onConfirm={handleDeleteSelectedConfirm}
        onCancel={handleDeleteSelectedCancel}
      />
    </Box>
  );
};

export default GuidesTable;
